/*** Swarm RTS Renderer module - (c) mjh.at - v0.3.2 2014-12-28 ***/

import { CANVAS_EFFECT_FACTORIES } from './render/canvas2d/effects.js';
import { HudPainter } from './render/hud/painter.js';
import { renderBackground, renderBreakoutTiles, renderBorder } from './render/canvas2d/background.js';
import { renderFightersBatched } from './render/canvas2d/fighters.js';
import { renderSpeedZones, renderPowerRangeIndicator, renderAiDebugStateMarker, renderAiTargetLines, renderSectors } from './render/canvas2d/overlays.js';
import { renderEntities } from './render/canvas2d/entities.js';
import { configureImageSmoothing } from './image_cache.js';
import { AI_STATE_COLORS } from './render_utils.js';
import {
    FACTION_NAMES,
    FACTION_COLORS,
    FIGHTER_COLORS,
    FRIGATE_COLORS,
    CARRIER_COLORS,
    VETERAN_COLORS,
    ELITE_COLORS,
    LEGENDARY_COLORS,
    BERSERK_COLORS,
    TURNCOAT_COLORS,
    ATTACK_COLORS,
    TURNCOAT_ATTACK_COLORS
} from './factions.js';

const RENDER_DEBUG = false;

/**
 * Handles all rendering and visual display
 */
class Canvas2DRenderer {
    /**
     * @param {{bg: HTMLCanvasElement, fg: HTMLCanvasElement, ui: HTMLCanvasElement}} canvases
     *        DOM canvas elements owned by Game and passed in. Renderer no longer
     *        reaches into `document` itself — keeps the abstraction clean and
     *        makes WebGLRenderer trivially construct against the same elements.
     * @param {Game} game
     * @param {UI} ui
     * @param {EventFeed} eventFeed
     * @param {PerformanceOverlay} performanceOverlay
     */
    constructor(canvases, game, ui, eventFeed, performanceOverlay) {
        var i;

        /** @type {Game} */
        this.game = game;
        /** Editor preview EntitySystem; overrides the live one while set.
         *  @type {EntitySystem|null} */
        this._entitySystemOverride = null;
        // One-time factory registration: Game's EffectSystem lives as long as
        // the Game, so this never needs refreshing. A wrapping WebGLRenderer
        // re-registers its own factories right after constructing us.
        game.effectSystem?.setFactories(CANVAS_EFFECT_FACTORIES);
        /** @type {UI} */
        this.ui = ui;
        /** @type {EventFeed} */
        this.eventFeed = eventFeed;
        /** @type {PerformanceOverlay} */
        this.performanceOverlay = performanceOverlay;

        this.factionNames = FACTION_NAMES;
        this.unitFactionColors = FACTION_COLORS;
        this.unitFighterColors = FIGHTER_COLORS;
        this.unitFrigateColors = FRIGATE_COLORS;
        this.unitCarrierColors = CARRIER_COLORS;
        this.unitVeteranColors = VETERAN_COLORS;
        this.unitEliteColors = ELITE_COLORS;
        this.unitLegendaryColors = LEGENDARY_COLORS;
        this.unitBerserkColors = BERSERK_COLORS;
        this.unitTurncoatColors = TURNCOAT_COLORS;
        this.unitAttackColors = ATTACK_COLORS;
        this.unitTurncoatAttackColors = TURNCOAT_ATTACK_COLORS;
        // Used by renderAiDebugStateMarker (game-world overlay); HudPainter
        // shares the same table for the AI debug legend in the HUD.
        this.aiStateColors = AI_STATE_COLORS;

        /** @type {import('./render/interface.js').RendererType} */
        this.type = 'canvas2d';

        /** @type {HTMLCanvasElement} */
        this.canvas = canvases.fg;
        /** inputCanvas — canvas that receives pointer events (satisfies Renderer interface). */
        this.inputCanvas = this.canvas;
        /** displayCanvas — canvas filling the game area (same as inputCanvas for Canvas2D). */
        this.displayCanvas = this.canvas;
        /** @type {import('./render/interface.js').LayerDef[]} */
        this.layerDefs = [];

        /** @type {CanvasRenderingContext2D} */
        this.context = this.canvas.getContext("2d");
        /** @type {HTMLCanvasElement} */
        this.bgCanvas = canvases.bg;
        /** @type {CanvasRenderingContext2D} */
        this.bgContext = this.bgCanvas.getContext("2d");
        /** @type {HTMLCanvasElement} */
        this.uiCanvas = canvases.ui;
        /** @type {CanvasRenderingContext2D} */
        this.uiContext = this.uiCanvas ? this.uiCanvas.getContext("2d") : this.context;
        configureImageSmoothing(this.context);
        configureImageSmoothing(this.bgContext);
        configureImageSmoothing(this.uiContext);

        /** @type {number} Total X offset in UI coords (safe-area + centering). */
        this.gameOffsetX = 0;
        /** @type {number} Total Y offset in UI coords (safe-area + centering). */
        this.gameOffsetY = 0;
        /** @type {number} Centering-only Y offset (excludes safe-area inset). Used for topBarShift. */
        this.gameOffsetYCentering = 0;
        /** @type {number} Current scale factor (CSS pixels per UI unit). */
        this._scaleFactor = 1;
        /** @type {boolean} Cached portrait flag set by setScaleFactor — read by getWorldTransform. */
        this._portrait = false;
        /** @type {[number,number,number,number,number,number]} Last game-world transform applied. */
        this._gameTf = [1, 0, 0, 1, 0, 0];

        /** @type {Array<[number, number, number]>} */
        this.backgroundStars = [];
        for (i = 0; i < 500; i += 1) {
            this.backgroundStars.push([
                Math.floor(Math.random() * this.gridSystem.width),
                Math.floor(Math.random() * this.gridSystem.height),
                Math.floor(Math.random() * 100 + 50)
            ]);
        }

        /** @type {HudPainter} Shared HUD painter — all UI overlay rendering lives here. */
        this._hud = new HudPainter({ uiCanvas: this.uiCanvas, uiContext: this.uiContext,
            game, ui, eventFeed, performanceOverlay });
    }

    // -----------------------------------------------------------------------
    // Live per-level references
    // -----------------------------------------------------------------------
    // Game.loadLevel() replaces locationGrid/entitySystem with fresh instances
    // each level (effectSystem is stable — created once, cleared per level).
    // The renderer reads all three live off `game` instead of holding copies,
    // so there is no re-wiring step to forget (forgotten re-pointing caused
    // real stale-reference bugs before). All three getters are pure.

    /** @returns {GridSystem} */
    get gridSystem() { return this.game.locationGrid; }

    /** @returns {EntitySystem} */
    get entitySystem() { return this._entitySystemOverride ?? this.game.entitySystem; }

    /**
     * Point the renderer at a stand-in EntitySystem (the editor's preview),
     * or back at the live one with `null`. Unlike a raw reference swap this
     * cannot go stale — clearing it always lands on the current level.
     * @param {EntitySystem|null} es
     */
    setEntitySystemOverride(es) { this._entitySystemOverride = es; }

    /** @returns {EffectSystem} */
    get effectSystem() { return this.game.effectSystem; }

    /**
     * Returns the current world-to-screen 2D transform (a, b, c, d, e, f matrix
     * suitable for ctx.setTransform), plus dimensions and orientation. Used by
     * the editor to apply the same transform to its own overlay canvas, and
     * by future overlays that need to draw in game-world coordinates without
     * needing a renderer-specific drawing API.
     * @returns {{a:number,b:number,c:number,d:number,e:number,f:number,
     *           scaleFactor:number, portrait:boolean}}
     */
    getWorldTransform() {
        return {
            a: this._gameTf[0], b: this._gameTf[1],
            c: this._gameTf[2], d: this._gameTf[3],
            e: this._gameTf[4], f: this._gameTf[5],
            scaleFactor: this._scaleFactor,
            portrait: this._portrait
        };
    }

    /** Delegate to the shared HudPainter. */
    renderUiLayer() {
        this._hud.renderAll();
    }

    /**
     * Canvas2D is synchronous — ready immediately after construction.
     * Satisfies the Renderer interface alongside WebGLRenderer's async variant.
     * @returns {Promise<void>}
     */
    whenReady() {
        return Promise.resolve();
    }

    /**
     * Main render method called each frame
     * @param {number} timePassed
     */
    render(timePassed) {
        const simulationTimePassed = this.game.getGameState() === 1 ? timePassed : 0;
        this.renderBackground(simulationTimePassed);
        this.renderGameLayer(simulationTimePassed);
        this.renderUiLayer();
    }

    renderGameLayer(simulationTimePassed) {
        this.context.clearRect(0, 0, this.gridSystem.width, this.gridSystem.height);

        const zoom = this.game.getLevelZoomScale ? this.game.getLevelZoomScale() : 1;
        if (zoom !== 1) this._pushLevelZoom(this.context, zoom);

        this.renderSpeedZones();
        this.renderEntities(simulationTimePassed);

        if (RENDER_DEBUG) {
            this.renderSectors();
        }

        this.effectSystem.updateByLayer(this.context, simulationTimePassed, 'mid');

        const effectsStart = performance.now();
        this.effectSystem.updateByLayer(this.context, simulationTimePassed, 'top');
        const effectsEnd = performance.now();
        this.game.effectsTimeMs = this.game.effectsTimeMs * 0.9 + (effectsEnd - effectsStart) * 0.1;

        if (zoom !== 1) this.context.restore();

        this.renderPowerRangeIndicator();
        this.renderBorder();
    }


    renderPowerRangeIndicator() { renderPowerRangeIndicator(this); }

    /**
     * @param {number} factor
     * @param {boolean} [portrait] - when true, the viewport is portrait. The game-world
     *   (fg + bg) contexts receive a 90° CW rotation so the game's right edge ends up
     *   at the screen's bottom; the UI context stays un-rotated and is laid out in a
     *   screen-aspect UI space (see `UI.getUiSize`).
     *
     *   Game-world transform (portrait): game (gx, gy) → canvas (gy*sf, (H - gx)*sf),
     *   via setTransform(0, -sf, sf, 0, 0, H*sf).
     */
    setScaleFactor(factor, portrait) {
        const W = this.gridSystem.width;
        const H = this.gridSystem.height;

        // Centering offset: moves game/UI origin so unused screen bands are symmetric.
        // getGameOffset() does NOT include safe.top (notch), so oy is pure centering margin.
        // gameOffsetYCentering equals oy; kept as a named field for topBarShift clarity.
        this._scaleFactor = factor;
        this._portrait = portrait;
        const dpr = this.game.getEffectiveDpr ? this.game.getEffectiveDpr() : 1;
        const [ox, oy] = this.ui.getGameOffset();
        this.gameOffsetX = ox;
        this.gameOffsetY = oy;
        this.gameOffsetYCentering = oy;
        this._hud?.setScaleFactor(factor, portrait, ox, oy);
        const oxPx = ox * factor * dpr;
        const oyPx = oy * factor * dpr;
        const sf = factor * dpr;

        // setTransform maps game-world coords to physical canvas pixels.
        // Multiplying the CSS-pixel scale by DPR keeps the game coordinate
        // system unchanged while drawing at native screen resolution.
        const gameTf = portrait
            ? [0, sf, -sf, 0, H * sf + oxPx, oyPx]
            : [sf, 0, 0, sf, oxPx, oyPx];
        this._gameTf = gameTf;

        // Clip game canvases to their footprint so entity/effect drawing never bleeds
        // into letterbox/pillarbox margins.  The clip is set at the base canvas state
        // (no enclosing save/restore) so it persists through all nested save/restore pairs.
        // The UI canvas is intentionally NOT clipped — buttons slide into the margins.
        for (const ctx of [this.context, this.bgContext]) {
            ctx.setTransform(...gameTf);
            ctx.beginPath();
            ctx.rect(0, 0, W, H);
            ctx.clip();
        }
        this.uiContext.setTransform(sf, 0, 0, sf, oxPx, oyPx);
    }

    /**
     * Apply a centered zoom transform to ctx for the level's zoomScale.
     * Call ctx.restore() after all zoomed rendering is done.
     * @param {CanvasRenderingContext2D} ctx
     * @param {number} zoom
     */
    _pushLevelZoom(ctx, zoom) {
        const W = this.gridSystem.width;
        const H = this.gridSystem.height;
        ctx.save();
        ctx.translate(W / 2, H / 2);
        ctx.scale(zoom, zoom);
        ctx.translate(-W / 2, -H / 2);
    }

    /** @param {number} timePassed */
    renderBackground(timePassed)    { renderBackground(this, timePassed); }
    renderSpeedZones()               { renderSpeedZones(this); }
    /** @param {number} timePassed */
    renderEntities(timePassed)       { renderEntities(this, timePassed); }
    renderFightersBatched()          { renderFightersBatched(this); }
    renderBreakoutTiles()            { renderBreakoutTiles(this); }
    /** @param {object} entity */
    renderAiDebugStateMarker(entity) { renderAiDebugStateMarker(this, entity); }
    renderAiTargetLines()            { renderAiTargetLines(this); }
    renderSectors()                  { renderSectors(this); }
    renderBorder()                   { renderBorder(this); }
}

export { Canvas2DRenderer };
